import React, { useState } from "react";
import axios from "axios";
import { Zap, ArrowRight, ArrowLeft, Check, Loader2, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const steps = [
  {
    id: 1,
    question: "What do you want to build?",
    key: "purpose",
    options: [
      { value: "blog", label: "Personal Blog", icon: "✍️", desc: "Blogging & content creation" },
      { value: "business", label: "Business Website", icon: "🏢", desc: "Company presence & leads" },
      { value: "ecommerce", label: "Online Store", icon: "🛍️", desc: "Selling products & services" },
      { value: "app", label: "Web Application", icon: "⚡", desc: "Dynamic app or SaaS" },
      { value: "developer", label: "Developer Project", icon: "💻", desc: "APIs, microservices, code" },
      { value: "wordpress", label: "WordPress Site", icon: "🔵", desc: "WP blog or business site" },
    ],
  },
  {
    id: 2,
    question: "Expected monthly visitors?",
    key: "visitors",
    options: [
      { value: "low", label: "Under 1,000", icon: "👤", desc: "Just starting out" },
      { value: "medium", label: "1,000 – 10,000", icon: "👥", desc: "Growing audience" },
      { value: "high", label: "10,000 – 100,000", icon: "👨‍👩‍👧‍👦", desc: "Established site" },
      { value: "enterprise", label: "100,000+", icon: "🌐", desc: "High traffic & scale" },
    ],
  },
  {
    id: 3,
    question: "Your technical experience?",
    key: "tech_level",
    options: [
      { value: "beginner", label: "Beginner", icon: "🌱", desc: "Just getting started, need managed hosting" },
      { value: "intermediate", label: "Intermediate", icon: "🔧", desc: "Some experience, comfortable with control panels" },
      { value: "advanced", label: "Advanced", icon: "🚀", desc: "Developer / sysadmin, want full control" },
    ],
  },
  {
    id: 4,
    question: "Monthly budget for hosting?",
    key: "budget",
    options: [
      { value: "low", label: "Under ₹200/mo", icon: "💰", desc: "Budget-friendly options" },
      { value: "medium", label: "₹200 – ₹600/mo", icon: "💳", desc: "Mid-range performance" },
      { value: "high", label: "₹600 – ₹2,000/mo", icon: "🏆", desc: "Premium performance" },
      { value: "premium", label: "₹2,000+/mo", icon: "💎", desc: "Enterprise grade" },
    ],
  },
];

const categoryLabels = {
  vps: "VPS Hosting",
  shared: "Shared Hosting",
  cloud: "Cloud Hosting",
  wordpress: "WordPress Hosting",
  domains: "Domain Names",
  ssl: "SSL Certificates",
  email: "Email Hosting",
};

const categoryHrefs = {
  vps: "/vps-hosting",
  shared: "/shared-hosting",
  cloud: "/cloud-hosting",
  wordpress: "/wordpress-hosting",
};

export default function PlanRecommender() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const currentStep = steps[step];
  const progress = ((step) / steps.length) * 100;

  const select = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    if (step < steps.length - 1) {
      setTimeout(() => setStep((s) => s + 1), 300);
    }
  };

  const submit = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/recommend`, answers);
      setResult(res.data);
    } catch {
      setError("Failed to get recommendation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setStep(0);
    setAnswers({});
    setResult(null);
    setError("");
  };

  return (
    <div data-testid="plan-recommender" className="page-enter pt-24 min-h-screen">
      {/* Hero */}
      <section className="relative py-16 overflow-hidden"
        style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(188,19,254,0.1) 0%, transparent 50%), #030305" }}>
        <div className="animated-grid absolute inset-0 opacity-20" />
        <div className="max-w-2xl mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full text-xs font-rajdhani font-700 tracking-widest"
            style={{ background: "rgba(188,19,254,0.1)", border: "1px solid rgba(188,19,254,0.3)", color: "#BC13FE" }}>
            <Zap size={12} /> AI PLAN RECOMMENDER
          </div>
          <h1 className="font-orbitron font-900 text-3xl lg:text-4xl text-white mb-3">
            Let AI Find Your <span className="text-gradient">Perfect Plan</span>
          </h1>
          <p className="font-outfit text-sm text-gray-500" style={{ fontWeight: 300 }}>
            Answer 4 simple questions. Get a personalized recommendation in seconds.
          </p>
        </div>
      </section>

      {/* Quiz / Result */}
      <section className="py-10">
        <div className="max-w-2xl mx-auto px-6">
          {!result && !loading && (
            <>
              {/* Progress bar */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-rajdhani text-xs text-gray-500">Step {step + 1} of {steps.length}</span>
                  <span className="font-rajdhani text-xs" style={{ color: "#BC13FE" }}>{Math.round(((step + 1) / steps.length) * 100)}% Complete</span>
                </div>
                <div className="h-1.5 rounded-full" style={{ background: "rgba(255,255,255,0.06)" }}>
                  <div
                    className="h-1.5 rounded-full transition-all duration-500"
                    style={{
                      width: `${((step + 1) / steps.length) * 100}%`,
                      background: "linear-gradient(to right, #00F3FF, #BC13FE)",
                    }}
                  />
                </div>
              </div>

              {/* Question */}
              <div className="glass rounded-2xl p-8 mb-6 animate-fade-in">
                <h2 className="font-rajdhani font-700 text-xl text-white mb-6">{currentStep.question}</h2>
                <div className="grid grid-cols-2 gap-3">
                  {currentStep.options.map((opt) => {
                    const selected = answers[currentStep.key] === opt.value;
                    return (
                      <button
                        key={opt.value}
                        data-testid={`option-${opt.value}`}
                        onClick={() => select(currentStep.key, opt.value)}
                        className="flex items-start gap-3 p-4 rounded-xl text-left transition-all duration-200 hover:scale-[1.02]"
                        style={{
                          background: selected ? "rgba(0,243,255,0.1)" : "rgba(255,255,255,0.03)",
                          border: selected ? "1px solid rgba(0,243,255,0.5)" : "1px solid rgba(255,255,255,0.07)",
                          boxShadow: selected ? "0 0 15px rgba(0,243,255,0.15)" : "none",
                        }}
                      >
                        <span className="text-xl flex-shrink-0">{opt.icon}</span>
                        <div>
                          <div className="font-rajdhani font-700 text-sm text-white mb-0.5">{opt.label}</div>
                          <div className="font-outfit text-xs text-gray-500" style={{ fontWeight: 300 }}>{opt.desc}</div>
                        </div>
                        {selected && <Check size={14} color="#00F3FF" className="ml-auto flex-shrink-0 mt-1" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  disabled={step === 0}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl font-rajdhani font-700 text-sm transition-all disabled:opacity-30"
                  style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#9ca3af" }}
                >
                  <ArrowLeft size={14} /> Back
                </button>

                {step === steps.length - 1 && answers[currentStep.key] && (
                  <button
                    data-testid="get-recommendation"
                    onClick={submit}
                    className="btn-primary flex items-center gap-2 px-6 py-2.5 text-sm font-700"
                  >
                    GET MY RECOMMENDATION <ArrowRight size={14} />
                  </button>
                )}
              </div>
            </>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-20 gap-6 animate-fade-in">
              <div className="relative w-20 h-20">
                <div className="absolute inset-0 rounded-full border-2 border-transparent animate-spin"
                  style={{ borderTopColor: "#00F3FF", borderRightColor: "#BC13FE" }} />
                <div className="absolute inset-3 rounded-full flex items-center justify-center"
                  style={{ background: "rgba(0,243,255,0.1)" }}>
                  <Zap size={24} color="#00F3FF" />
                </div>
              </div>
              <div className="text-center">
                <div className="font-rajdhani font-700 text-white text-lg mb-2">AI is analyzing your needs...</div>
                <div className="font-outfit text-sm text-gray-500" style={{ fontWeight: 300 }}>Comparing {plans => plans || "all"} plans for the best match</div>
              </div>
              <div className="flex gap-2">
                {["Scanning plans", "Analyzing budget", "Matching features"].map((t, i) => (
                  <div key={t} className="text-xs font-outfit px-3 py-1 rounded-full"
                    style={{ background: "rgba(0,243,255,0.08)", border: "1px solid rgba(0,243,255,0.2)", color: "#00F3FF", animationDelay: `${i * 0.5}s` }}>
                    {t}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="text-center py-10">
              <p className="text-red-400 font-outfit mb-4">{error}</p>
              <button onClick={restart} className="btn-neon px-6 py-2">Try Again</button>
            </div>
          )}

          {/* Result */}
          {result && !loading && (
            <div className="animate-slide-up">
              <div className="text-center mb-8">
                <div className="font-orbitron font-900 text-2xl text-white mb-2">
                  Your <span className="text-gradient">Perfect Match!</span>
                </div>
                <p className="font-outfit text-sm text-gray-500">Based on your requirements, AI recommends:</p>
              </div>

              {/* Recommended plan card */}
              <div
                className="rounded-2xl p-8 mb-6 relative overflow-hidden"
                style={{
                  background: "linear-gradient(135deg, rgba(0,243,255,0.08), rgba(188,19,254,0.06))",
                  border: "1px solid rgba(0,243,255,0.3)",
                  boxShadow: "0 0 40px rgba(0,243,255,0.1)",
                }}
              >
                <div className="absolute top-0 left-0 right-0 h-px"
                  style={{ background: "linear-gradient(to right, transparent, #00F3FF, #BC13FE, transparent)" }} />

                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="plan-badge mb-3">AI RECOMMENDED</div>
                    <h3 className="font-orbitron font-900 text-2xl text-white">{result.plan_name}</h3>
                    <div className="font-rajdhani font-600 text-sm mt-1" style={{ color: "#00F3FF" }}>
                      {categoryLabels[result.category] || result.category}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-orbitron font-900 text-3xl text-gradient">
                      ₹{result.price?.toLocaleString("en-IN") || "—"}
                    </div>
                    <div className="font-outfit text-xs text-gray-500">/month</div>
                  </div>
                </div>

                <p className="font-outfit text-sm text-gray-300 leading-relaxed mb-6" style={{ fontWeight: 300 }}>
                  {result.reason}
                </p>

                {result.key_features && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {result.key_features.map((f) => (
                      <div key={f} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs"
                        style={{ background: "rgba(0,255,148,0.08)", border: "1px solid rgba(0,255,148,0.2)", color: "#00FF94" }}>
                        <Check size={10} /> {f}
                      </div>
                    ))}
                  </div>
                )}

                {categoryHrefs[result.category] && (
                  <Link to={categoryHrefs[result.category]} className="no-underline">
                    <button className="btn-primary flex items-center gap-2 px-8 py-3 text-sm font-700 w-full justify-center">
                      VIEW PLAN & GET STARTED <ChevronRight size={16} />
                    </button>
                  </Link>
                )}
              </div>

              <div className="text-center">
                <button onClick={restart} className="font-outfit text-sm text-gray-500 hover:text-gray-300 transition-colors">
                  Start over with different answers
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
